"use client";

import { c as _c } from "react/compiler-runtime";
import { UserFollowBtnSize } from "@/components/user/utils/UserFollowBtn";
import type { DropInteractionParams } from "@/components/waves/drops/Drop";
import { useEmoji } from "@/contexts/EmojiContext";
import { ApiNotificationCause } from "@/generated/models/ApiNotificationCause";
import { numberWithCommas } from "@/helpers/Helpers";
import type { ExtendedDrop } from "@/helpers/waves/drop.helpers";
import type { ActiveDropState } from "@/types/dropInteractionTypes";
import type { INotificationDropBoosted, INotificationDropReacted, INotificationDropVoted } from "@/types/feed.types";
import NotificationsFollowBtn from "../NotificationsFollowBtn";
import NotificationDrop from "../subcomponents/NotificationDrop";
import NotificationHeader from "../subcomponents/NotificationHeader";
import NotificationTimestamp from "../subcomponents/NotificationTimestamp";
import { getIsDirectMessage, useWaveNavigation } from "../utils/navigationUtils";
export const getNotificationVoteColor = (vote: number) => {
  if (vote > 0) return "tw-text-green";
  if (vote < 0) return "tw-text-red";
  return "tw-text-iron-500";
};
type NotificationUnion = INotificationDropVoted | INotificationDropReacted | INotificationDropBoosted;
interface Props {
  readonly notification: NotificationUnion;
  readonly activeDrop: ActiveDropState | null;
  readonly onReply: (param: DropInteractionParams) => void;
  readonly onQuote: (param: DropInteractionParams) => void;
  readonly onDropContentClick?: ((drop: ExtendedDrop) => void) | undefined;
}
export default function NotificationDropReacted(t0) {
  const $ = _c(65);
  const {
    notification,
    activeDrop,
    onReply,
    onQuote,
    onDropContentClick
  } = t0;
  const {
    createReplyClickHandler,
    createQuoteClickHandler
  } = useWaveNavigation();
  const {
    findCustomEmoji,
    findNativeEmoji
  } = useEmoji();
  const isVoted = notification.cause === ApiNotificationCause.DropVoted;
  const isReacted = notification.cause === ApiNotificationCause.DropReacted;
  const isBoosted = notification.cause === ApiNotificationCause.DropBoosted;
  let actionElement;
  if (isVoted) {
    const voteValue = notification.additional_context.vote;
    let t1;
    if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
      t1 = <span className="tw-text-sm tw-font-normal tw-text-iron-400">rated</span>;
      $[0] = t1;
    } else {
      t1 = $[0];
    }
    let t2;
    if ($[1] !== voteValue) {
      t2 = getNotificationVoteColor(voteValue);
      $[1] = voteValue;
      $[2] = t2;
    } else {
      t2 = $[2];
    }
    const t3 = `${t2} tw-text-sm tw-font-medium`;
    const t4 = voteValue > 0 && "+";
    let t5;
    if ($[3] !== voteValue) {
      t5 = numberWithCommas(voteValue);
      $[3] = voteValue;
      $[4] = t5;
    } else {
      t5 = $[4];
    }
    let t6;
    if ($[5] !== t3 || $[6] !== t4 || $[7] !== t5) {
      t6 = <span className={t3}>{t4}{t5}</span>;
      $[5] = t3;
      $[6] = t4;
      $[7] = t5;
      $[8] = t6;
    } else {
      t6 = $[8];
    }
    let t7;
    if ($[9] !== notification.created_at) {
      t7 = <NotificationTimestamp createdAt={notification.created_at} />;
      $[9] = notification.created_at;
      $[10] = t7;
    } else {
      t7 = $[10];
    }
    let t8;
    if ($[11] !== t6 || $[12] !== t7) {
      t8 = <>{t1}{t6}{t7}</>;
      $[11] = t6;
      $[12] = t7;
      $[13] = t8;
    } else {
      t8 = $[13];
    }
    actionElement = t8;
  } else {
    if (isBoosted) {
      let t1;
      if ($[14] === Symbol.for("react.memo_cache_sentinel")) {
        t1 = <span className="tw-text-sm tw-font-normal tw-text-iron-400">🔥 boosted 🔥</span>;
        $[14] = t1;
      } else {
        t1 = $[14];
      }
      let t2;
      if ($[15] !== notification.created_at) {
        t2 = <>{t1}<NotificationTimestamp createdAt={notification.created_at} /></>;
        $[15] = notification.created_at;
        $[16] = t2;
      } else {
        t2 = $[16];
      }
      actionElement = t2;
    } else {
      if (isReacted) {
        let emojiNode;
        if ($[17] !== findCustomEmoji || $[18] !== findNativeEmoji || $[19] !== notification.additional_context.reaction) {
          const rawId = notification.additional_context.reaction.replaceAll(":", "");
          emojiNode = null;
          const custom = findCustomEmoji(rawId);
          if (custom) {
            emojiNode = <img src={custom.skins[0]?.src} alt={rawId} className="tw-max-h-5 tw-max-w-5 tw-object-contain" />;
          } else {
            const native = findNativeEmoji(rawId);
            if (native) {
              const t1 = native.skins[0]?.native;
              let t2;
              if ($[21] !== t1) {
                t2 = <span className="tw-flex tw-items-center tw-justify-center tw-text-[1.2rem]">{t1}</span>;
                $[21] = t1;
                $[22] = t2;
              } else {
                t2 = $[22];
              }
              emojiNode = t2;
            }
          }
          $[17] = findCustomEmoji;
          $[18] = findNativeEmoji;
          $[19] = notification.additional_context.reaction;
          $[20] = emojiNode;
        } else {
          emojiNode = $[20];
        }
        if (!emojiNode) {
          return null;
        }
        let t1;
        if ($[23] === Symbol.for("react.memo_cache_sentinel")) {
          t1 = <span className="tw-text-sm tw-font-normal tw-text-iron-400">reacted</span>;
          $[23] = t1;
        } else {
          t1 = $[23];
        }
        let t2;
        if ($[24] !== notification.created_at) {
          t2 = <NotificationTimestamp createdAt={notification.created_at} />;
          $[24] = notification.created_at;
          $[25] = t2;
        } else {
          t2 = $[25];
        }
        let t3;
        if ($[26] !== emojiNode || $[27] !== t2) {
          t3 = <>{t1}{emojiNode}{t2}</>;
          $[26] = emojiNode;
          $[27] = t2;
          $[28] = t3;
        } else {
          t3 = $[28];
        }
        actionElement = t3;
      } else {
        return null;
      }
    }
  }
  const drop = notification.related_drops[0];
  if (!drop) {
    return null;
  }
  let T0;
  let t1;
  let t2;
  let t3;
  let t4;
  let t5;
  let t6;
  let t7;
  let t8;
  if ($[29] !== actionElement || $[30] !== activeDrop || $[31] !== createQuoteClickHandler || $[32] !== createReplyClickHandler || $[33] !== drop || $[34] !== notification.related_identity || $[35] !== onQuote || $[36] !== onReply) {
    const isDirectMessage = getIsDirectMessage(drop.wave);
    t7 = "tw-flex tw-w-full tw-flex-col tw-space-y-2";
    let t9;
    if ($[46] !== notification.related_identity) {
      t9 = <NotificationsFollowBtn profile={notification.related_identity} size={UserFollowBtnSize.SMALL} />;
      $[46] = notification.related_identity;
      $[47] = t9;
    } else {
      t9 = $[47];
    }
    if ($[48] !== actionElement || $[49] !== notification.related_identity || $[50] !== t9) {
      t8 = <NotificationHeader author={notification.related_identity} actions={t9}>{actionElement}</NotificationHeader>;
      $[48] = actionElement;
      $[49] = notification.related_identity;
      $[50] = t9;
      $[51] = t8;
    } else {
      t8 = $[51];
    }
    T0 = NotificationDrop;
    t1 = drop;
    t2 = activeDrop;
    t3 = onReply;
    t4 = onQuote;
    t5 = createReplyClickHandler(drop.wave.id, isDirectMessage);
    t6 = createQuoteClickHandler(isDirectMessage);
    $[29] = actionElement;
    $[30] = activeDrop;
    $[31] = createQuoteClickHandler;
    $[32] = createReplyClickHandler;
    $[33] = drop;
    $[34] = notification.related_identity;
    $[35] = onQuote;
    $[36] = onReply;
    $[37] = T0;
    $[38] = t1;
    $[39] = t2;
    $[40] = t3;
    $[41] = t4;
    $[42] = t5;
    $[43] = t6;
    $[44] = t7;
    $[45] = t8;
  } else {
    T0 = $[37];
    t1 = $[38];
    t2 = $[39];
    t3 = $[40];
    t4 = $[41];
    t5 = $[42];
    t6 = $[43];
    t7 = $[44];
    t8 = $[45];
  }
  let t9;
  if ($[52] !== T0 || $[53] !== onDropContentClick || $[54] !== t1 || $[55] !== t2 || $[56] !== t3 || $[57] !== t4 || $[58] !== t5 || $[59] !== t6) {
    t9 = <T0 drop={t1} activeDrop={t2} onReply={t3} onQuote={t4} onReplyClick={t5} onQuoteClick={t6} onDropContentClick={onDropContentClick} />;
    $[52] = T0;
    $[53] = onDropContentClick;
    $[54] = t1;
    $[55] = t2;
    $[56] = t3;
    $[57] = t4;
    $[58] = t5;
    $[59] = t6;
    $[60] = t9;
  } else {
    t9 = $[60];
  }
  let t10;
  if ($[61] !== t7 || $[62] !== t8 || $[63] !== t9) {
    t10 = <div className={t7}>{t8}{t9}</div>;
    $[61] = t7;
    $[62] = t8;
    $[63] = t9;
    $[64] = t10;
  } else {
    t10 = $[64];
  }
  return t10;
}